import Bugsnag from '@bugsnag/js';

// Dispositif temporaire : mesure l'origine des nouveaux inscrits, à retirer une fois l'analyse faite.

export const SIGNUP_ORIGIN_STORAGE_KEY = 'welya.signupOrigin';
const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'] as const;
const MAX_VALUE_LENGTH = 100;
const MAX_QUERY_KEYS = 20;

const bugsnagApiKey = import.meta.env.VITE_BUGSNAG_API_KEY;
const isBugsnagEnabled = bugsnagApiKey !== undefined && bugsnagApiKey !== '';

type UtmKey = (typeof UTM_KEYS)[number];

export type SignupMethod = 'email' | 'google';
export type SignupPage = 'login' | 'register';

type VisitedPage = {
  path: string;
  /** Noms des paramètres, sans leurs valeurs (un token ne doit pas fuiter). */
  queryKeys: string[];
};

export type SignupOrigin = {
  landingPath: string;
  referrer: string | null;
  utm: Partial<Record<UtmKey, string>>;
  queryKeys: string[];
  /** Route protégée qui a renvoyé vers la connexion (`state.from` de `RequireAuth`). */
  from: VisitedPage | null;
  capturedAt: string;
};

type PageLocation = Pick<Location, 'pathname' | 'search'>;

/** Ne garde que l'origine et le chemin : la query d'un référent peut porter des données personnelles. */
export function sanitizeReferrer(referrer: string): string | null {
  if (!referrer) return null;
  try {
    const url = new URL(referrer);
    return `${url.origin}${url.pathname}`;
  } catch {
    return null;
  }
}

function queryKeysOf(params: URLSearchParams): string[] {
  return [...new Set(params.keys())]
    .slice(0, MAX_QUERY_KEYS)
    .map((key) => key.slice(0, MAX_VALUE_LENGTH));
}

export function buildSignupOrigin(
  location: PageLocation,
  referrer: string,
  now: Date,
): SignupOrigin {
  const params = new URLSearchParams(location.search);
  const utm: Partial<Record<UtmKey, string>> = {};
  for (const key of UTM_KEYS) {
    const value = params.get(key)?.trim();
    if (value) utm[key] = value.slice(0, MAX_VALUE_LENGTH);
  }

  return {
    landingPath: location.pathname,
    referrer: sanitizeReferrer(referrer),
    utm,
    queryKeys: queryKeysOf(params),
    from: null,
    capturedAt: now.toISOString(),
  };
}

function writeStoredSignupOrigin(origin: SignupOrigin): void {
  window.sessionStorage.setItem(SIGNUP_ORIGIN_STORAGE_KEY, JSON.stringify(origin));
}

/** Première page vue de la visite uniquement : les redirections suivantes effacent l'origine. */
export function captureSignupOrigin(location: PageLocation, referrer: string, now: Date): void {
  try {
    if (window.sessionStorage.getItem(SIGNUP_ORIGIN_STORAGE_KEY) !== null) return;
    writeStoredSignupOrigin(buildSignupOrigin(location, referrer, now));
  } catch {
    // stockage indisponible (navigation privée, bloqueur) : l'origine est perdue, rien d'autre
  }
}

/** Complète l'origine avec la première redirection vers la connexion, sans toucher à l'arrivée. */
export function recordSignupRedirect(from: PageLocation): void {
  const origin = readStoredSignupOrigin();
  if (!origin || origin.from) return;
  try {
    writeStoredSignupOrigin({
      ...origin,
      from: { path: from.pathname, queryKeys: queryKeysOf(new URLSearchParams(from.search)) },
    });
  } catch {
    // idem
  }
}

export function readStoredSignupOrigin(): SignupOrigin | null {
  try {
    const raw = window.sessionStorage.getItem(SIGNUP_ORIGIN_STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return null;
    const { landingPath, capturedAt } = parsed as Partial<SignupOrigin>;
    if (typeof landingPath !== 'string' || typeof capturedAt !== 'string') return null;
    return parsed as SignupOrigin;
  } catch {
    return null;
  }
}

export function clearStoredSignupOrigin(): void {
  try {
    window.sessionStorage.removeItem(SIGNUP_ORIGIN_STORAGE_KEY);
  } catch {
    // idem
  }
}

export function buildSignupOriginMetadata(
  origin: SignupOrigin | null,
  signup: { method: SignupMethod; page: SignupPage },
): Record<string, unknown> {
  return {
    method: signup.method,
    page: signup.page,
    landingPath: origin?.landingPath ?? null,
    referrer: origin?.referrer ?? null,
    ...origin?.utm,
    queryKeys: origin?.queryKeys ?? [],
    fromPath: origin?.from?.path ?? null,
    fromQueryKeys: origin?.from?.queryKeys ?? [],
    capturedAt: origin?.capturedAt ?? null,
  };
}

export function reportSignupOrigin(signup: { method: SignupMethod; page: SignupPage }): void {
  const metadata = buildSignupOriginMetadata(readStoredSignupOrigin(), signup);
  clearStoredSignupOrigin();
  if (!isBugsnagEnabled) return;
  try {
    Bugsnag.notify(new Error('Signup origin'), (event) => {
      event.severity = 'info';
      event.addMetadata('signupOrigin', metadata);
    });
  } catch {
    // un échec d'envoi ne doit jamais bloquer l'inscription
  }
}
